import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deleteForecast, getAllForecasts } from '../api/client'
import type { ForecastRun } from '../types'
import { useSessionStore } from '../store/sessionStore'

const ADMIN_PASSWORD = 'beny'

export default function AdminPage() {
  const navigate = useNavigate()
  const isAdmin = useSessionStore((s) => s.isAdmin)
  const setAdmin = useSessionStore((s) => s.setAdmin)

  const [password, setPassword] = useState('')
  const [forecasts, setForecasts] = useState<ForecastRun[]>([])
  const [query, setQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const load = async () => {
    setIsLoading(true)
    try {
      const rows = await getAllForecasts()
      setForecasts(rows)
    } catch (e) {
      console.error('Failed to load admin forecasts:', e)
      alert(`Failed to load admin history: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return forecasts
    return forecasts.filter((f: any) => {
      const text =
        `${f.job_id} ${f.question_id || ''} ${f.original_question || ''} ${f.formatted_question || ''}`.toLowerCase()
      return text.includes(q)
    })
  }, [forecasts, query])

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAdmin(true)
      setPassword('')
      return
    }
    alert('Incorrect password')
  }

  const handleDelete = async (jobId: string) => {
    const ok = confirm(`Delete forecast ${jobId.slice(0, 8)}…? This cannot be undone.`)
    if (!ok) return
    try {
      await deleteForecast(jobId)
      setForecasts((xs) => xs.filter((x) => x.job_id !== jobId))
    } catch (e) {
      console.error('Failed to delete forecast:', e)
      alert(`Failed to delete forecast: ${e instanceof Error ? e.message : 'Unknown error'}`)
    }
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif font-semibold text-2xl sm:text-3xl text-ink">Admin history</h1>
            <div className="mt-1 text-sm text-muted">All forecasts in the database (admin-only).</div>
          </div>
          {isAdmin && (
            <button
              className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
              type="button"
              onClick={() => setAdmin(false)}
            >
              Log out
            </button>
          )}
        </div>

        {!isAdmin ? (
          <section className="mt-6 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5 max-w-xl">
            <label htmlFor="admin-password" className="block font-mono text-xs text-muted tracking-wider">
              Admin password
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="mt-2 w-full rounded-xl border border-rule bg-paper px-3 py-2.5 text-sm text-ink shadow-[inset_0_1px_0_rgba(20,18,16,.05)] focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60"
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] transition"
                type="button"
                onClick={handleLogin}
              >
                Log in
              </button>
              <button
                className="rounded-xl border border-rule bg-paper shadow-delphi px-4 py-2.5 text-sm font-semibold text-ink hover:bg-white/40 transition"
                type="button"
                onClick={() => navigate('/')}
              >
                Back to home
              </button>
            </div>
          </section>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <input
                className="flex-1 min-w-[220px] rounded-xl border border-rule bg-paper px-3 py-2.5 text-sm text-ink shadow-[inset_0_1px_0_rgba(20,18,16,.05)] focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by question, job id, or question id…"
              />
              <button
                className="rounded-xl border border-rule bg-paper shadow-delphi px-4 py-2.5 text-sm font-semibold text-ink hover:bg-white/40 transition disabled:opacity-60 disabled:cursor-not-allowed"
                type="button"
                onClick={() => void load()}
                disabled={isLoading}
              >
                Refresh
              </button>
            </div>

            {filtered.length === 0 ? (
              <div className="mt-4 text-sm text-muted">{isLoading ? 'Loading…' : 'No forecasts found.'}</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((f: any) => {
                  const prob = f.final_result?.probability
                  const cost = f.token_summary?.estimated_cost_usd
                  const questionText = f.formatted_question || f.final_result?.question || f.original_question || f.job_id
                  return (
                    <section key={f.job_id} className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                          {String(f.status || 'unknown').toUpperCase()}
                        </span>
                        <span className="font-mono text-[10px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                          {f.job_id.slice(0, 8)}…
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-ink leading-relaxed line-clamp-4">{questionText}</p>
                      <div className="mt-3 font-mono text-[11px] text-muted space-y-1">
                        <div>Created: {new Date(f.created_at).toLocaleString()}</div>
                        {f.completed_at && <div>Completed: {new Date(f.completed_at).toLocaleString()}</div>}
                        {typeof prob === 'number' && <div>Probability: {(prob * 100).toFixed(1)}%</div>}
                        {typeof cost === 'number' && <div>Cost: ${cost.toFixed(6)}</div>}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition disabled:opacity-60 disabled:cursor-not-allowed"
                          type="button"
                          onClick={() => navigate(`/results/${f.question_id}`)}
                          disabled={!f.question_id}
                          title={!f.question_id ? 'No linked question_id for this forecast' : ''}
                        >
                          Open
                        </button>
                        <button
                          className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
                          type="button"
                          onClick={() => handleDelete(f.job_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </section>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

